import type { IUserFull } from "../entities/user";
import { BaseOrganizationEndpoints } from "./base";
import { PaginatedResult } from "./response/paginated_result";

export type BulkImportType = "profile_photos" | "visitor_preregistration";

export type BulkImportStatus = "queued" | "processing" | "completed" | "failed";

export type BulkImportItemStatus =
  | "pending"
  | "skipped"
  | "succeeded"
  | "failed";

export type BulkImportItemReferenceType = "email";

/** Order of day, month and year in an imported file's dates; ISO dates are always accepted */
export type BulkImportDateFormat = "MDY" | "DMY" | "YMD";

export type BulkImportCounts = {
  total: number;
  matched: number;
  skipped: number;
  succeeded: number;
  failed: number;
  ignored: number;
};

export type BulkImport = {
  id: string;
  type: BulkImportType;
  status: BulkImportStatus;
  source_path: string;
  creator: IUserFull;
  counts: BulkImportCounts;
  error_code: string | null;
  created: string;
  updated: string;
  office?: { id: string; name: string } | null;
  visitor_type?: { id: string; name: string } | null;
};

export type BulkImportItem = {
  id: string;
  bulk_import_id: string;
  reference_type: BulkImportItemReferenceType;
  reference_id: string;
  status: BulkImportItemStatus;
  error_code: string | null;
  /** Explanation of `error_code` safe to show to users, null for internal errors */
  error_message: string | null;
  created: string;
  updated: string;
};

export type CreateUploadResponse = {
  upload_url: string;
  s3_path: string;
};

export type ListItemsParams = {
  cursor?: string;
  limit?: number;
  status?: BulkImportItemStatus;
  reference_type?: BulkImportItemReferenceType;
};

const BASE_PATH = "/v1/bulk-imports";

export class BulkImportsClient extends BaseOrganizationEndpoints {
  async createUpload(type: BulkImportType) {
    return this.requestWithStatus<CreateUploadResponse>(
      "POST",
      `${BASE_PATH}/uploads`,
      { type }
    );
  }

  async createProfilePhotosImport(s3Path: string) {
    return this.requestWithStatus<BulkImport>(
      "POST",
      `${BASE_PATH}/profile-photos`,
      { s3_path: s3Path }
    );
  }

  async createVisitorPreregistrationImport(args: {
    s3_path: string;
    office_id: string;
    visitor_type_id: string;
    date_format?: BulkImportDateFormat;
  }) {
    return this.requestWithStatus<BulkImport>(
      "POST",
      `${BASE_PATH}/visitor-preregistrations`,
      args
    );
  }

  async list(cursor?: string, limit?: number, type?: BulkImportType) {
    const urlParams = this.formatGenericParams({
      type: type ?? null,
      cursor: cursor ?? null,
      limit: limit ?? null
    });
    return this.requestWithStatus<PaginatedResult<BulkImport>>(
      "GET",
      `${BASE_PATH}?${urlParams.toString()}`
    );
  }

  async get(id: string) {
    return this.requestWithStatus<BulkImport>("GET", `${BASE_PATH}/${id}`);
  }

  async listItems(id: string, params: ListItemsParams = {}) {
    const urlParams = this.formatGenericParams({
      cursor: params.cursor ?? null,
      limit: params.limit ?? null,
      status: params.status ?? null,
      reference_type: params.reference_type ?? null
    });
    return this.requestWithStatus<PaginatedResult<BulkImportItem>>(
      "GET",
      `${BASE_PATH}/${id}/items?${urlParams.toString()}`
    );
  }
}
